/**
 * Multi-click and compound handler shapes for the remaining tools.
 *
 * Where a tool's exact legacy computation is complex or ambiguous,
 * these handlers render a clinically useful approximation and label it
 * clearly. Individual tools can graduate to bespoke math over time —
 * see each factory comment for what a "proper" version would do.
 */
import type { ChiroToolHandler, ChiroDrawable, ChiroPoint } from '../types';
import {
  distance, midpoint, angle4Point, extendLine,
} from '../math';

const LINE_COLOR = '#ffd400';
const LABEL_STYLE = { color: LINE_COLOR, fontSize: 14 };

/**
 * 4 clicks → two lines and their intersection point. Used for
 * "Stress Lines Flexion" (210) and "Stress Lines Extension" (211),
 * where the intersection represents the focus of biomechanical stress.
 */
export function twoLinesIntersectionHandler(toolId: number, label: string): ChiroToolHandler {
  return {
    toolId,
    expectedPointCount: 4,
    render(points) {
      const out: ChiroDrawable[] = [];
      if (points.length >= 2) {
        const l1 = extendLine(points[0], points[1], 1.5);
        out.push({ kind: 'line', a: l1.a, b: l1.b, style: { color: LINE_COLOR } });
      }
      if (points.length >= 4) {
        const l2 = extendLine(points[2], points[3], 1.5);
        out.push({ kind: 'line', a: l2.a, b: l2.b, style: { color: LINE_COLOR } });
        const cross = intersect(points[0], points[1], points[2], points[3]);
        if (cross) {
          // Highlight the intersection point with a larger marker via a
          // cross of short lines; the wizard's own point-drawing loop
          // handles user-placed clicks separately.
          const s = 6;
          const c1: ChiroPoint = { worldX: cross.worldX - s, worldY: cross.worldY, worldZ: cross.worldZ };
          const c2: ChiroPoint = { worldX: cross.worldX + s, worldY: cross.worldY, worldZ: cross.worldZ };
          const c3: ChiroPoint = { worldX: cross.worldX, worldY: cross.worldY - s, worldZ: cross.worldZ };
          const c4: ChiroPoint = { worldX: cross.worldX, worldY: cross.worldY + s, worldZ: cross.worldZ };
          out.push({ kind: 'line', a: c1, b: c2, style: { color: '#ff5555' } });
          out.push({ kind: 'line', a: c3, b: c4, style: { color: '#ff5555' } });
          out.push({ kind: 'label', at: cross, text: 'Focus of stress', style: LABEL_STYLE });
        }
      }
      return out;
    },
    compute(points) {
      const cross = intersect(points[0], points[1], points[2], points[3]);
      if (!cross) return { summary: `${label}: lines are parallel — no intersection.` };
      return {
        summary: `${label} — intersection marked.`,
        rows: [
          { label: 'X (mm)', value: cross.worldX.toFixed(1) },
          { label: 'Y (mm)', value: cross.worldY.toFixed(1) },
        ],
      };
    },
  };
}

function intersect(a: ChiroPoint, b: ChiroPoint, c: ChiroPoint, d: ChiroPoint): ChiroPoint | null {
  const x1 = a.worldX, y1 = a.worldY, x2 = b.worldX, y2 = b.worldY;
  const x3 = c.worldX, y3 = c.worldY, x4 = d.worldX, y4 = d.worldY;
  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 1e-6) return null;
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  return {
    worldX: x1 + t * (x2 - x1),
    worldY: y1 + t * (y2 - y1),
    worldZ: a.worldZ,
  };
}

/**
 * 4 clicks → ratio of distance(1,2) to distance(3,4). Used for
 * "Anterior Atlanto-occipital Dislocation" (109) — ratio ≥ 1 is
 * abnormal — and "Farfan Disc Height" (401) — posterior/anterior
 * disc height ratio for degeneration assessment.
 */
export function ratioHandler(toolId: number, label: string): ChiroToolHandler {
  return {
    toolId,
    expectedPointCount: 4,
    render(points) {
      const out: ChiroDrawable[] = [];
      if (points.length >= 2) {
        out.push({ kind: 'line', a: points[0], b: points[1], style: { color: LINE_COLOR } });
        const d1 = distance(points[0], points[1]);
        out.push({
          kind: 'label',
          at: midpoint(points[0], points[1]),
          text: `${d1.toFixed(1)} mm`,
          style: LABEL_STYLE,
        });
      }
      if (points.length >= 4) {
        out.push({ kind: 'line', a: points[2], b: points[3], style: { color: '#66c0ff' } });
        const d2 = distance(points[2], points[3]);
        out.push({
          kind: 'label',
          at: midpoint(points[2], points[3]),
          text: `${d2.toFixed(1)} mm`,
          style: { color: '#66c0ff', fontSize: 14 },
        });
      }
      return out;
    },
    compute(points) {
      const d1 = distance(points[0], points[1]);
      const d2 = distance(points[2], points[3]);
      const ratio = d2 > 0 ? d1 / d2 : NaN;
      return {
        summary: `${label}: ratio ${ratio.toFixed(2)} (${d1.toFixed(1)} / ${d2.toFixed(1)} mm)`,
        rows: [
          { label: 'First distance',  value: `${d1.toFixed(1)} mm` },
          { label: 'Second distance', value: `${d2.toFixed(1)} mm` },
          { label: 'Ratio',           value: ratio.toFixed(2) },
        ],
      };
    },
  };
}

/**
 * 2N clicks → N per-pair distance measurements. Used by tools that
 * measure the same quantity at multiple anatomical levels (e.g. cervical
 * canal AP dimension at C1..C7 = 7 pairs; prevertebral soft tissues
 * at 6 levels = 6 pairs; hip joint space superior/axial/medial = 3 pairs).
 *
 * Callers pass a labels array of length N naming each pair.
 */
export function perPairDistanceHandler(
  toolId: number,
  toolLabel: string,
  pairLabels: string[],
): ChiroToolHandler {
  const totalClicks = pairLabels.length * 2;
  return {
    toolId,
    expectedPointCount: totalClicks,
    render(points) {
      const out: ChiroDrawable[] = [];
      for (let i = 0; i + 1 < points.length; i += 2) {
        const a = points[i], b = points[i + 1];
        out.push({ kind: 'line', a, b, style: { color: LINE_COLOR } });
        const d = distance(a, b);
        out.push({
          kind: 'label',
          at: midpoint(a, b),
          text: `${d.toFixed(1)} mm`,
          style: { color: LINE_COLOR, fontSize: 12 },
        });
      }
      return out;
    },
    compute(points) {
      const rows: { label: string; value: string }[] = [];
      for (let i = 0; i < pairLabels.length; i++) {
        const a = points[2 * i], b = points[2 * i + 1];
        rows.push({ label: pairLabels[i], value: `${distance(a, b).toFixed(1)} mm` });
      }
      return {
        summary: `${toolLabel} measured at ${pairLabels.length} level(s).`,
        rows,
      };
    },
  };
}

/**
 * 4N clicks → N per-group 4-point angles. Used for Lumbar Intervertebral
 * Disc Angles (402): 5 discs × 4 corners = 20 clicks, giving 5 disc
 * angles (typically L1-L2 through L5-S1).
 */
export function perLevelAngleHandler(
  toolId: number,
  toolLabel: string,
  levelLabels: string[],
): ChiroToolHandler {
  const totalClicks = levelLabels.length * 4;
  return {
    toolId,
    expectedPointCount: totalClicks,
    render(points) {
      const out: ChiroDrawable[] = [];
      for (let i = 0; i + 3 < points.length; i += 4) {
        const l1 = extendLine(points[i],     points[i + 1], 0.3);
        const l2 = extendLine(points[i + 2], points[i + 3], 0.3);
        out.push({ kind: 'line', a: l1.a, b: l1.b, style: { color: LINE_COLOR } });
        out.push({ kind: 'line', a: l2.a, b: l2.b, style: { color: LINE_COLOR } });
        const ang = angle4Point(points[i], points[i + 1], points[i + 2], points[i + 3]);
        const at = midpoint(midpoint(points[i], points[i + 1]), midpoint(points[i + 2], points[i + 3]));
        out.push({
          kind: 'label',
          at,
          text: `${levelLabels[i / 4] ?? ''} ${ang.toFixed(1)}°`,
          style: LABEL_STYLE,
        });
      }
      return out;
    },
    compute(points) {
      const rows: { label: string; value: string }[] = [];
      for (let i = 0; i < levelLabels.length; i++) {
        const p = i * 4;
        const ang = angle4Point(points[p], points[p + 1], points[p + 2], points[p + 3]);
        rows.push({ label: levelLabels[i], value: `${ang.toFixed(1)}°` });
      }
      return {
        summary: `${toolLabel} — ${rows.map((r) => `${r.label}: ${r.value}`).join(', ')}`,
        rows,
      };
    },
  };
}

/**
 * N clicks → polyline connecting successive clicks in order. Used as a
 * clinically useful visual/quantitative approximation for multi-landmark
 * tools whose legacy math is elaborate (Gonstead 700, Cervical Curve
 * 701, George's Line 203, Posterior Cervical Line 204, Risser-Ferguson
 * 301, Iliac Angle+Index 507, Acetabular Angle 506, Axial Knee 511,
 * Glenohumeral Joint Space 600, Lateral Bending Sign 409, Lumbosacral
 * Lordosis 404).
 *
 * Reports total path length + individual segment lengths. Not equivalent
 * to the legacy compound geometry; upgrade to a bespoke handler when a
 * customer needs the exact reading for a specific tool.
 */
export function polylineHandler(
  toolId: number,
  label: string,
  totalClicks: number,
): ChiroToolHandler {
  return {
    toolId,
    expectedPointCount: totalClicks,
    render(points) {
      const out: ChiroDrawable[] = [];
      for (let i = 0; i + 1 < points.length; i++) {
        out.push({ kind: 'line', a: points[i], b: points[i + 1], style: { color: LINE_COLOR } });
      }
      if (points.length === totalClicks && points.length > 1) {
        const total = totalPathLength(points);
        out.push({
          kind: 'label',
          at: points[Math.floor(points.length / 2)],
          text: `${total.toFixed(1)} mm`,
          style: LABEL_STYLE,
        });
      }
      return out;
    },
    compute(points) {
      const rows: { label: string; value: string }[] = [];
      for (let i = 0; i + 1 < points.length; i++) {
        rows.push({
          label: `Segment ${i + 1}`,
          value: `${distance(points[i], points[i + 1]).toFixed(1)} mm`,
        });
      }
      const total = totalPathLength(points);
      return {
        summary: `${label} path length: ${total.toFixed(1)} mm (${points.length} landmarks).`,
        rows: [{ label: 'Total path', value: `${total.toFixed(1)} mm` }, ...rows],
      };
    },
  };
}

function totalPathLength(points: ChiroPoint[]): number {
  let sum = 0;
  for (let i = 0; i + 1 < points.length; i++) sum += distance(points[i], points[i + 1]);
  return sum;
}
